import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Tabs, 
  Tab, 
  Badge,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  useTheme,
  Fade,
  Collapse,
  AlertTitle,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  MarkEmailRead as MarkEmailReadIcon,
  FilterList as FilterListIcon,
  DeleteSweep as DeleteSweepIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import NotificationCard from './NotificationCard';

interface Notification {
  id: string;
  message: string;
  category: string;
  notification_type: string;
  is_read: boolean;
  is_active: boolean;
  created_at: string;
  reference_id?: string;
}

interface NotificationsPanelProps {
  notifications: Notification[];
  onDismissNotification: (id: string) => Promise<void>;
  onClickNotification?: (id: string) => void;
  onMarkAllAsRead?: () => Promise<void>;
  onRefresh?: () => Promise<void>;
  onClearAll?: () => Promise<void>;
  title?: string;
  maxHeight?: number | string;
  loading?: boolean;
  filterCategories?: string[];
  filterTypes?: string[];
  defaultTab?: 'all' | 'unread';
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  notifications,
  onDismissNotification,
  onClickNotification,
  onMarkAllAsRead,
  onRefresh,
  onClearAll,
  title = "Notifications",
  maxHeight = 400,
  loading = false,
  filterCategories,
  filterTypes,
  defaultTab = 'unread'
}) => {
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>(defaultTab);
  const [expandedNotifications, setExpandedNotifications] = useState<Set<string>>(new Set());
  const [processingAllRead, setProcessingAllRead] = useState(false);
  const theme = useTheme();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  // Auto-switch to 'all' tab if there are no unread notifications
  useEffect(() => {
    const unreadCount = notifications.filter(n => 
      n.is_active && !n.is_read && 
      (!filterCategories || filterCategories.includes(n.category)) &&
      (!filterTypes || filterTypes.includes(n.notification_type))
    ).length;
    
    if (unreadCount === 0 && activeTab === 'unread') {
      setActiveTab('all');
    } else if (unreadCount > 0 && defaultTab === 'unread' && activeTab === 'all') {
      // Switch back to unread if there are unread notifications and that's the default
      setActiveTab('unread');
    }
  }, [notifications, activeTab, filterCategories, filterTypes, defaultTab]);

  const filteredNotifications = notifications.filter(n => 
    n.is_active && 
    (activeTab === 'all' || (activeTab === 'unread' && !n.is_read)) &&
    (!filterCategories || filterCategories.includes(n.category)) &&
    (!filterTypes || filterTypes.includes(n.notification_type))
  );

  const unreadCount = notifications.filter(n => 
    n.is_active && !n.is_read && 
    (!filterCategories || filterCategories.includes(n.category)) &&
    (!filterTypes || filterTypes.includes(n.notification_type))
  ).length;

  const handleChangeTab = (event: React.SyntheticEvent, newValue: 'all' | 'unread') => {
    setActiveTab(newValue);
  };

  const handleMarkAllAsRead = async () => {
    if (!onMarkAllAsRead || unreadCount === 0 || processingAllRead) return;
    
    try {
      setProcessingAllRead(true);
      await onMarkAllAsRead();
    } catch (error) {
      console.error("Error marking all as read:", error);
    } finally {
      setProcessingAllRead(false);
    }
  };

  const handleRefresh = async () => {
    if (!onRefresh) return;
    await onRefresh();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleClearAll = async () => {
    if (!onClearAll) return;
    handleMenuClose();
    await onClearAll();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}
    >
      {/* Header with title and actions */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: theme.palette.mode === 'dark' 
            ? `linear-gradient(to right, ${theme.palette.grey[900]}, ${theme.palette.grey[800]})` 
            : `linear-gradient(to right, ${theme.palette.grey[50]}, ${theme.palette.grey[100]})`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <NotificationsIcon 
            color="primary" 
            sx={{ mr: 1.5, fontSize: '1.3rem' }} 
          />
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
            {title}
          </Typography>
          {unreadCount > 0 && (
            <Badge
              badgeContent={unreadCount}
              color="error"
              sx={{ ml: 1.5 }}
            >
              <Box sx={{ width: 8 }} /> {/* Spacer for badge */}
            </Badge>
          )}
        </Box>
        
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1
        }}>
          {onRefresh && (
            <Tooltip title="Refresh notifications">
              <IconButton
                size="small"
                onClick={handleRefresh}
                disabled={loading}
                color="primary"
                sx={{
                  '&:hover': {
                    bgcolor: 'primary.lighter',
                  },
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {onMarkAllAsRead && (
            <Tooltip title="Mark all as read">
              <span>
                <IconButton
                  size="small"
                  onClick={handleMarkAllAsRead}
                  disabled={unreadCount === 0 || loading || processingAllRead}
                  color="primary"
                  sx={{
                    position: 'relative',
                    '&:hover': {
                      bgcolor: 'primary.lighter',
                    },
                  }}
                >
                  {processingAllRead ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <MarkEmailReadIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          
          <Tooltip title="More options">
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              color="primary"
              sx={{
                '&:hover': {
                  bgcolor: 'primary.lighter',
                },
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Menu
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              elevation: 3,
              sx: {
                borderRadius: 2,
                minWidth: 180,
                mt: 0.5,
                boxShadow: theme.shadows[3]
              }
            }}
          >
            {onMarkAllAsRead && (
              <MenuItem 
                onClick={handleMarkAllAsRead}
                disabled={unreadCount === 0 || processingAllRead}
              >
                <ListItemIcon>
                  <MarkEmailReadIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText>Mark all as read</ListItemText>
              </MenuItem>
            )}
            
            {onClearAll && (
              <MenuItem onClick={handleClearAll}>
                <ListItemIcon>
                  <DeleteSweepIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>Clear all notifications</ListItemText>
              </MenuItem>
            )}
            
            {onRefresh && (
              <MenuItem onClick={handleRefresh}>
                <ListItemIcon>
                  <RefreshIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText>Refresh</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Box>

      {/* Tabs for filtering */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab 
            label="All" 
            value="all"
            disabled={true}
            sx={{ 
              textTransform: 'none',
              fontWeight: 'medium',
              fontSize: '0.875rem',
              opacity: 0.5,
              cursor: 'not-allowed'
            }} 
          />
          <Tab 
            label={
              <Badge 
                badgeContent={unreadCount} 
                color="error"
                sx={{ 
                  '& .MuiBadge-badge': {
                    right: -12,
                    top: -2,
                    fontSize: '0.7rem',
                    height: '18px',
                    minWidth: '18px',
                    padding: '0 5px'
                  }
                }}
              >
                Unread
              </Badge>
            } 
            value="unread"
            sx={{ 
              textTransform: 'none',
              fontWeight: 'medium',
              fontSize: '0.875rem'
            }} 
          />
        </Tabs>
      </Box>

      {/* Notifications list */}
      <Box
        sx={{
          p: 2,
          flexGrow: 1,
          overflowY: 'auto',
          maxHeight: maxHeight,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.divider,
            borderRadius: '3px',
          },
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress size={36} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Loading notifications...
            </Typography>
          </Box>
        ) : filteredNotifications.length === 0 ? (
          <Alert
            severity="info"
            icon={<CheckCircleIcon fontSize="inherit" />}
            sx={{ 
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '16px'
            }}
          >
            <AlertTitle>
              {activeTab === 'all' 
                ? 'No notifications to display' 
                : 'No unread notifications'}
            </AlertTitle>
            <Typography variant="body2" align="center" sx={{ mt: 1 }}>
              {activeTab === 'all' 
                ? 'You don\'t have any notifications at this time.' 
                : 'All caught up! No unread notifications to display.'}
            </Typography>
            {onRefresh && (
              <Button 
                startIcon={<RefreshIcon />}
                size="small"
                onClick={handleRefresh}
                sx={{ mt: 2 }}
              >
                Refresh
              </Button>
            )}
          </Alert>
        ) : (
          <Collapse in={true} appear={true}>
            {filteredNotifications.map((notification) => (
              <Fade 
                key={notification.id} 
                in={true} 
                timeout={300}
                style={{ 
                  transformOrigin: 'top',
                  marginBottom: '8px'
                }}
              >
                <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
                  <NotificationCard
                    id={notification.id}
                    message={notification.message}
                    category={notification.category}
                    created_at={notification.created_at}
                    is_read={notification.is_read}
                    onClose={onDismissNotification}
                    onClick={onClickNotification}
                    referenceId={notification.reference_id}
                  />
                </div>
              </Fade>
            ))}
          </Collapse>
        )}
      </Box>

      {/* Optional footer with pagination or bulk actions */}
      {(unreadCount > 0 || filteredNotifications.length > 0) && (
        <>
          <Divider />
          <Box sx={{ 
            p: 1.5, 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'
          }}>
            <Typography variant="caption" color="text.secondary">
              {filteredNotifications.length} {filteredNotifications.length === 1 ? 'notification' : 'notifications'}
            </Typography>
            
            {unreadCount > 0 && onMarkAllAsRead && (
              <Button
                size="small"
                startIcon={<MarkEmailReadIcon fontSize="small" />}
                onClick={handleMarkAllAsRead}
                disabled={processingAllRead}
                sx={{ 
                  textTransform: 'none',
                  fontSize: '0.75rem'
                }}
              >
                {processingAllRead ? 'Processing...' : 'Mark all read'}
              </Button>
            )}
          </Box>
        </>
      )}

      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </Paper>
  );
};

export default NotificationsPanel; 